import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/cn";
import { getBoardScanSources } from "@/api/explore";
import {
  useDiscoveryProfile,
  useSaveDiscoveryProfile,
} from "@/hooks/useDiscovery";
import { DEFAULT_JOB_BOARDS } from "@career-ops-amplify/shared";

interface BoardScanPickerProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

function shortBoardDescription(
  description: string | undefined,
  providerId: string,
): string {
  if (!description) return providerId;
  const cleaned = description
    .replace(/^"+|"+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
  // Provider seed text often starts with long API docs — keep a short blurb.
  const firstSentence = cleaned.split(/(?<=\.)\s+/)[0] ?? cleaned;
  if (firstSentence.length <= 100) return firstSentence;
  return `${firstSentence.slice(0, 97)}…`;
}

export function BoardScanPicker({ selectedIds, onChange }: BoardScanPickerProps) {
  const { data: profile } = useDiscoveryProfile();
  const saveMutation = useSaveDiscoveryProfile();
  const [showUnavailable, setShowUnavailable] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["explore", "boards"],
    queryFn: getBoardScanSources,
    staleTime: 60_000,
  });

  const boards = data?.boards ?? [];
  const scannableBoards = boards.filter((b) => b.scannable);
  const visibleBoards = showUnavailable ? boards : scannableBoards;
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const unavailableCount = boards.length - scannableBoards.length;

  function selectAllScannable() {
    onChange(scannableBoards.map((b) => b.providerId));
  }

  function clearAll() {
    onChange([]);
  }

  async function enableDefaults() {
    const defaults = DEFAULT_JOB_BOARDS.filter((id) =>
      scannableBoards.some((b) => b.providerId === id),
    );
    onChange(defaults);
    if (!profile?.jobBoards?.length) {
      await saveMutation.mutateAsync({ jobBoards: defaults });
    }
  }

  async function persistSelection(ids: string[]) {
    await saveMutation.mutateAsync({ jobBoards: ids });
  }

  function handleToggle(providerId: string) {
    const next = selectedSet.has(providerId)
      ? selectedIds.filter((id) => id !== providerId)
      : [...selectedIds, providerId];
    onChange(next);
    void persistSelection(next);
  }

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">Loading board providers…</p>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive">
        Failed to load board providers. Run provider seed or check Settings → Providers.
      </p>
    );
  }

  if (boards.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/10 p-4 text-sm text-muted-foreground">
        No board providers in the catalog. Seed providers with{" "}
        <code className="text-xs">npx tsx scripts/seed-providers.ts</code> from the backend package.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={selectAllScannable}
          className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted/20"
        >
          Select all scannable
        </button>
        <button
          type="button"
          onClick={enableDefaults}
          className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted/20"
        >
          Enable recommended
        </button>
        <button
          type="button"
          onClick={clearAll}
          className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted/20"
        >
          Clear
        </button>
        <span className="text-xs text-muted-foreground">
          {selectedIds.length} selected · {scannableBoards.length} scannable
        </span>
        {unavailableCount > 0 ? (
          <label className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={showUnavailable}
              onChange={(e) => setShowUnavailable(e.target.checked)}
              className="rounded border-border"
            />
            Show unavailable ({unavailableCount})
          </label>
        ) : null}
      </div>

      <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-border bg-bg p-2">
        {visibleBoards.map((board) => {
          const isSelected = selectedSet.has(board.providerId);
          const disabled = !board.scannable;

          return (
            <label
              key={board.providerId}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 transition",
                isSelected && !disabled
                  ? "border-brand/40 bg-brand/5"
                  : "border-transparent hover:bg-muted/10",
                disabled && "cursor-not-allowed opacity-60",
              )}
            >
              <input
                type="checkbox"
                checked={isSelected}
                disabled={disabled}
                onChange={() => handleToggle(board.providerId)}
                className="mt-1 rounded border-border"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">{board.name}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {shortBoardDescription(board.description, board.providerId)}
                </p>
                {board.skipReason && (
                  <p className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">
                    {board.skipReason}
                  </p>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {selectedIds.length === 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Select at least one board to run a scan. Try &quot;Enable recommended&quot; for RemoteOK and Hacker News.
        </p>
      )}
    </div>
  );
}
